import { useState, useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useTenant } from "@/hooks/useTenant";
import { useFirmMetrics } from "@/hooks/useFirmMetrics";
import { supabase } from "@/integrations/supabase/client";
import {
  Users,
  Search,
  ArrowUpDown,
  ArrowRight,
  Building2,
  TrendingUp,
  TrendingDown,
  Package,
  Loader2,
  ExternalLink,
  PieChart,
  Plus,
} from "lucide-react";
import { CreateFamilyDialog } from "@/components/firm/CreateFamilyDialog";
import { OnboardingStatusBadge } from "@/components/firm/OnboardingStatusBadge";

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '-';
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(2)}B`;
  }
  if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(1)}M`;
  }
  if (value >= 1e3) {
    return `$${(value / 1e3).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

interface FamilyDetail {
  id: string;
  name: string;
  aum: number;
  positionCount: number;
  returnYtd?: number;
  createdAt: string;
  onboardingStatus?: string;
}

export default function FamilyClientList() {
  const navigate = useNavigate();
  const { orgId, isMFO, families, isLoading: tenantLoading } = useTenant();
  const { firmMetrics, isLoading: metricsLoading } = useFirmMetrics();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<"name" | "aum" | "positions">("aum");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedFamily, setSelectedFamily] = useState<FamilyDetail | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Fetch detailed position data for selected family - MUST be before any conditional returns (React hooks rules)
  const { data: familyPositions, isLoading: positionsLoading } = useQuery({
    queryKey: ['family-positions', selectedFamily?.id],
    queryFn: async () => {
      if (!selectedFamily?.id || !orgId) return [];

      const { data, error } = await supabase
        .from('position_metrics')
        .select(`
          *,
          positions:position_id (
            id,
            instruments:instrument_id (
              id,
              name,
              issuer,
              type
            )
          )
        `)
        .eq('org_id', orgId)
        .eq('family_id', selectedFamily.id)
        .order('nav_base', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data || [];
    },
    enabled: !!selectedFamily?.id && !!orgId,
  });

  // Combine families data with metrics
  const familyData = useMemo(() => {
    if (!families || !firmMetrics?.aum_by_family) return [];

    const aumMap = new Map(
      firmMetrics.aum_by_family.map((f) => [f.family_id, f])
    );

    return families.map((family) => {
      const metrics = aumMap.get(family.id);
      return {
        id: family.id,
        name: family.name,
        aum: metrics?.aum || 0,
        positionCount: metrics?.position_count || 0,
        returnYtd: undefined, // Would need additional data
        createdAt: family.created_at,
        onboardingStatus: family.onboarding_status,
      } as FamilyDetail;
    });
  }, [families, firmMetrics]);

  // Filter and sort
  const filteredFamilies = useMemo(() => {
    let result = [...familyData];

    // Filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((f) => f.name.toLowerCase().includes(q));
    }

    // Sort
    result.sort((a, b) => {
      let cmp = 0;
      if (sortField === "name") {
        cmp = a.name.localeCompare(b.name);
      } else if (sortField === "aum") {
        cmp = a.aum - b.aum;
      } else if (sortField === "positions") {
        cmp = a.positionCount - b.positionCount;
      }
      return sortDir === "desc" ? -cmp : cmp;
    });

    return result;
  }, [familyData, searchQuery, sortField, sortDir]);

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  // Calculate asset allocation for selected family
  const assetAllocation = useMemo(() => {
    if (!familyPositions || familyPositions.length === 0) return [];

    const allocation: Record<string, number> = {};
    let total = 0;

    familyPositions.forEach((pm: { nav_base?: number; positions?: { instruments?: { type?: string } } }) => {
      const nav = Number(pm.nav_base) || 0;
      total += nav;
      const type = pm.positions?.instruments?.type || 'other';

      // Map types to friendly names
      const typeMap: Record<string, string> = {
        'private_fund': 'Private Equity',
        'public_fund': 'Public Markets',
        'equity': 'Public Equity',
        'fixed_income': 'Fixed Income',
        'real_estate': 'Real Estate',
        'cash': 'Cash',
        'other': 'Other',
      };

      const displayType = typeMap[type] || type;
      allocation[displayType] = (allocation[displayType] || 0) + nav;
    });

    return Object.entries(allocation)
      .map(([name, value]) => ({
        name,
        value,
        pct: total > 0 ? (value / total) * 100 : 0,
      }))
      .sort((a, b) => b.value - a.value);
  }, [familyPositions]);

  const isLoading = tenantLoading || metricsLoading;

  // Redirect SFO users (AFTER all hooks)
  if (!tenantLoading && !isMFO) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-6 w-6" />
            Family Clients
          </h1>
          <p className="text-muted-foreground">
            {filteredFamilies.length} families managed
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="gap-1">
            <Building2 className="h-3 w-3" />
            Total AUM: {formatCurrency(firmMetrics?.total_aum_base || 0)}
          </Badge>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            Add Family
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search families..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Families Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Families</CardTitle>
          <CardDescription>Click a row to view family details</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleSort("name")}
                >
                  <div className="flex items-center gap-1">
                    Family Name
                    <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead
                  className="text-right cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleSort("aum")}
                >
                  <div className="flex items-center justify-end gap-1">
                    AUM
                    <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead
                  className="text-right cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleSort("positions")}
                >
                  <div className="flex items-center justify-end gap-1">
                    Positions
                    <ArrowUpDown className="h-3 w-3" />
                  </div>
                </TableHead>
                <TableHead className="text-right">% of Firm</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFamilies.map((family) => {
                const pctOfFirm =
                  firmMetrics?.total_aum_base && firmMetrics.total_aum_base > 0
                    ? (family.aum / firmMetrics.total_aum_base) * 100
                    : 0;

                return (
                  <TableRow
                    key={family.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedFamily(family)}
                  >
                    <TableCell className="font-medium">{family.name}</TableCell>
                    <TableCell>
                      <OnboardingStatusBadge status={family.onboardingStatus} />
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(family.aum)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline">{family.positionCount}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {pctOfFirm.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/firm/families/${family.id}`);
                        }}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredFamilies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No families found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Family Detail Sheet */}
      <Sheet open={!!selectedFamily} onOpenChange={(open) => !open && setSelectedFamily(null)}>
        <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {selectedFamily?.name}
            </SheetTitle>
            <SheetDescription>
              Family portfolio overview and top holdings
            </SheetDescription>
          </SheetHeader>

          {selectedFamily && (
            <div className="mt-6 space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Total AUM
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="text-2xl font-bold">
                      {formatCurrency(selectedFamily.aum)}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Positions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="text-2xl font-bold">
                      {selectedFamily.positionCount}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Asset Allocation */}
              <div>
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <PieChart className="h-4 w-4" />
                  Asset Allocation
                </h3>
                {positionsLoading ? (
                  <div className="h-20 flex items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : assetAllocation.length > 0 ? (
                  <div className="space-y-2">
                    {assetAllocation.map((item) => (
                      <div key={item.name} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>{item.name}</span>
                          <span className="text-muted-foreground">
                            {formatCurrency(item.value)} ({item.pct.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${item.pct}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground text-sm">
                    No allocation data available
                  </div>
                )}
              </div>

              {/* Top Holdings */}
              <div>
                <h3 className="text-sm font-medium mb-3">Top Holdings</h3>
                {positionsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : familyPositions && familyPositions.length > 0 ? (
                  <div className="space-y-2">
                    {familyPositions.slice(0, 10).map((pm: { id: string; positions?: { instruments?: { name?: string } }; mv_base?: number }) => (
                      <div
                        key={pm.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                      >
                        <div>
                          <div className="font-medium text-sm">
                            {pm.positions?.instruments?.name || "Unknown"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {pm.positions?.instruments?.issuer || "-"}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">
                            {formatCurrency(pm.nav_base || 0)}
                          </div>
                          {pm.irr_itd && (
                            <div
                              className={`text-xs ${
                                pm.irr_itd >= 0 ? "text-success" : "text-destructive"
                              }`}
                            >
                              {pm.irr_itd >= 0 ? "+" : ""}
                              {(pm.irr_itd * 100).toFixed(1)}% IRR
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No position data available
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  onClick={() => navigate(`/firm/families/${selectedFamily.id}`)}
                >
                  <ArrowRight className="h-4 w-4" />
                  View Full Details
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Create Family Dialog */}
      <CreateFamilyDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </div>
  );
}
